/**
 * L7 Platform — progressive AJAX pager for L7 Blog.
 *
 * Intercepts `.l7-blog-pagination` clicks, fetches the next page from
 * `GET /l7-platform/v1/blog`, and swaps `.l7-blog-block` in place. The
 * authored `<a href="?l7_blog_page=N">` links still work without JS (full
 * page load). Never mutates the Visual Builder DOM (`body.et-fb`).
 */
(function (window, document) {
	'use strict';

	var QUERY_VAR = 'l7_blog_page';
	var controller = null;

	/**
	 * @return {boolean}
	 */
	function isVisualBuilder() {
		return document.body && document.body.classList.contains('et-fb');
	}

	/**
	 * @return {boolean}
	 */
	function prefersReducedMotion() {
		return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	/**
	 * @param {string} href Absolute or relative pager href.
	 * @return {number}
	 */
	function pageFromHref(href) {
		try {
			var url = new URL(href, window.location.href);
			var raw = url.searchParams.get(QUERY_VAR);
			return raw ? Math.max(1, parseInt(raw, 10) || 1) : 1;
		} catch (err) {
			return 1;
		}
	}

	/**
	 * Prefer the authored `data-page` so hash-only / intercepted hrefs still resolve.
	 *
	 * @param {Element} link Pager `<a>`.
	 * @return {number}
	 */
	function pageFromLink(link) {
		var raw = link.getAttribute('data-page');
		if (raw) {
			var n = parseInt(raw, 10);
			if (n >= 1) {
				return n;
			}
		}
		return pageFromHref(link.getAttribute('href') || link.href);
	}

	/**
	 * @return {number}
	 */
	function pageFromLocation() {
		return pageFromHref(window.location.href);
	}

	/**
	 * @param {Element} block `.l7-blog-block`.
	 * @param {number} page 1-based page.
	 * @return {string}
	 */
	function endpointUrl(block, page) {
		var endpoint = block.getAttribute('data-endpoint') || '';
		if (!endpoint) {
			return '';
		}
		var params = new URLSearchParams();
		params.set('page', String(page));
		params.set('count', block.getAttribute('data-count') || '4');
		params.set('category', block.getAttribute('data-category') || '');
		params.set('layout', block.getAttribute('data-layout') || 'featured');
		params.set('columns', block.getAttribute('data-columns') || '3');
		params.set('min_width', block.getAttribute('data-min-width') || '280');
		params.set('media', block.getAttribute('data-media') || 'image');
		params.set('bg_seed', block.getAttribute('data-bg-seed') || '');
		params.set('bg_palette', block.getAttribute('data-bg-palette') || '');
		params.set('bg_style', block.getAttribute('data-bg-style') || '');
		params.set('base', block.getAttribute('data-base') || window.location.pathname);
		return endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + params.toString();
	}

	/**
	 * @param {Element} block `.l7-blog-block`.
	 * @param {string} message Live-region text.
	 * @return {void}
	 */
	function setStatus(block, message) {
		var status = block.querySelector('.l7-blog-block__status');
		if (status) {
			status.textContent = message;
		}
	}

	/**
	 * @param {Element} block `.l7-blog-block`.
	 * @param {number} page Requested page.
	 * @param {string} href History URL (pager href).
	 * @param {boolean} push Whether to pushState.
	 * @return {void}
	 */
	function fetchPage(block, page, href, push) {
		var url = endpointUrl(block, page);
		if (!url) {
			window.location.assign(href);
			return;
		}

		if (controller) {
			controller.abort();
		}
		controller = window.AbortController ? new AbortController() : null;

		block.classList.add('is-loading');
		block.setAttribute('aria-busy', 'true');
		setStatus(block, 'Loading page ' + page);

		var opts = { credentials: 'same-origin' };
		if (controller) {
			opts.signal = controller.signal;
		}

		window
			.fetch(url, opts)
			.then(function (res) {
				if (!res.ok) {
					throw new Error('blog pager ' + res.status);
				}
				return res.json();
			})
			.then(function (data) {
				if (!data || typeof data.html !== 'string' || data.html.indexOf('l7-blog-block') === -1) {
					throw new Error('blog pager empty');
				}
				var wrap = document.createElement('div');
				wrap.innerHTML = data.html;
				var next = wrap.querySelector('.l7-blog-block');
				if (!next) {
					throw new Error('blog pager markup');
				}
				block.replaceWith(next);
				if (push && window.history && window.history.pushState) {
					window.history.pushState({ l7BlogPage: page }, '', href);
				}
				try {
					if (typeof next.scrollIntoView === 'function') {
						next.scrollIntoView({
							behavior: prefersReducedMotion() ? 'auto' : 'smooth',
							block: 'start',
						});
					}
					next.setAttribute('tabindex', '-1');
					if (typeof next.focus === 'function') {
						next.focus({ preventScroll: true });
					}
				} catch (focusErr) {
					// Swap already succeeded; do not fall through to location.assign.
				}
				setStatus(next, 'Page ' + (data.page || page) + ' of ' + (data.pages || ''));
			})
			.catch(function (err) {
				if (err && err.name === 'AbortError') {
					return;
				}
				window.location.assign(href);
			});
	}

	/**
	 * @param {Event} event Click.
	 * @return {void}
	 */
	function onClick(event) {
		if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return;
		}
		var node = event.target;
		if (node && node.nodeType === 3) {
			node = node.parentElement;
		}
		var link = node && node.closest ? node.closest('a') : null;
		if (!link || !link.closest('.l7-blog-pagination')) {
			return;
		}
		var block = link.closest('.l7-blog-block[data-l7-blog]');
		if (!block) {
			return;
		}
		var href = link.href || link.getAttribute('href');
		var page = pageFromLink(link);
		var current = parseInt(block.getAttribute('data-page'), 10) || 1;
		event.preventDefault();
		if (typeof event.stopImmediatePropagation === 'function') {
			event.stopImmediatePropagation();
		}
		event.stopPropagation();
		if (page === current) {
			return;
		}
		fetchPage(block, page, href, true);
	}

	/**
	 * @return {void}
	 */
	function onPopState() {
		var blocks = document.querySelectorAll('.l7-blog-block[data-l7-blog]');
		if (!blocks.length) {
			return;
		}
		var page = pageFromLocation();
		for (var i = 0; i < blocks.length; i++) {
			var block = blocks[i];
			var current = parseInt(block.getAttribute('data-page'), 10) || 1;
			if (page === current) {
				continue;
			}
			fetchPage(block, page, window.location.href, false);
		}
	}

	/**
	 * @return {void}
	 */
	function init() {
		if (isVisualBuilder()) {
			return;
		}
		document.addEventListener('click', onClick, true);
		window.addEventListener('popstate', onPopState);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})(window, document);
